import * as Types from './types';

export class EventEmitterService<
    EventArgumentsMap extends Record<string, any[]> = Record<string, any[]>
> {
    events: {
        [K in keyof EventArgumentsMap]: Types.EventHandler<EventArgumentsMap[K]>[]
    } = {} as {
        [K in keyof EventArgumentsMap]: Types.EventHandler<EventArgumentsMap[K]>[]
    };

    trigger<K extends keyof EventArgumentsMap>(
        eventName: K,
        ...args: EventArgumentsMap[K]
    ): void {
        if (eventName in this.events) {
            this.events[eventName].forEach((callback) => callback(...args));
        }
    }

    on<K extends keyof EventArgumentsMap>(
        eventName: K | K[],
        callback: Types.EventHandler<EventArgumentsMap[K]>
    ): void {
        if (Array.isArray(eventName)) {
            eventName.forEach((name) => this.subscribe(name, callback));
        } else {
            this.subscribe(eventName, callback);
        }
    }

    once<K extends keyof EventArgumentsMap>(
        eventName: K | K[],
        callback: Types.EventHandler<EventArgumentsMap[K]>
    ): void {
        const onceWrapped = this.onceWrapper(eventName, callback);

        this.on(eventName, onceWrapped);
    }

    off<K extends keyof EventArgumentsMap>(
        eventName: K | K[],
        callback?: Types.EventHandler<EventArgumentsMap[K]>
    ): void {
        if (callback) {
            if (Array.isArray(eventName)) {
                eventName.forEach((name) => this.removeListener(name, callback));
            } else {
                this.removeListener(eventName, callback);
            }
        } else {
            if (Array.isArray(eventName)) {
                eventName.forEach((name) => this.removeAllListeners(name));
            } else {
                this.removeAllListeners(eventName);
            }
        }
    }

    onceWrapper<K extends keyof EventArgumentsMap>(
        eventName: K | K[],
        callback: Types.EventHandler<EventArgumentsMap[K]>
    ): Types.EventHandler<EventArgumentsMap[K]> {
        const wrapped = (...args: EventArgumentsMap[K]): void => {
            this.off(eventName, wrapped);

            callback(...args);
        };

        return wrapped;
    }

    subscribe<K extends keyof EventArgumentsMap>(
        eventName: K,
        callback: Types.EventHandler<EventArgumentsMap[K]>
    ): void {
        if (!(eventName in this.events)) {
            this.events[eventName] = [];
        }

        this.events[eventName].push(callback);
    }

    removeListener<K extends keyof EventArgumentsMap>(
        eventName: K,
        callback: Types.EventHandler<EventArgumentsMap[K]>
    ): void {
        if (!(eventName in this.events)) {
            return;
        }

        this.events[eventName] = this.events[eventName].filter((currentEvent) => currentEvent !== callback);

        if (this.events[eventName].length === 0) {
            delete this.events[eventName];
        }
    }

    removeAllListeners<K extends keyof EventArgumentsMap>(eventName: K): void {
        if (eventName in this.events) {
            delete this.events[eventName];
        }
    }
}
